import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTemplatePermissions } from "@/hooks/useTemplatePermissions";
import { usePermissions } from "@/hooks/usePermissions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Shield, Building, Home, Users, AlertTriangle } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface Organization {
  id: number;
  name: string;
  billing_email: string;
  status: string;
  created_at: string;
}

interface HomeData {
  id: number;
  name: string;
  address: string;
  organization_id: number | null;
  registration_number: string | null;
  bed_count: number | null;
  is_active: boolean;
  created_at: string;
}

export function TemplatePermissionManager() {
  const { canAccessDeveloperFeatures } = usePermissions();
  const [activeTab, setActiveTab] = useState("organizations");
  
  const {
    permissions,
    isLoadingPermissions,
    grantPermission,
    revokePermission,
    isGrantingPermission,
    isRevokingPermission,
    hasPermission,
  } = useTemplatePermissions();

  // Fetch organizations
  const { data: organizationsResponse = [], isLoading: isLoadingOrgs } = useQuery({
    queryKey: ['/api/organizations'],
  });

  // Fetch homes
  const { data: homesResponse = [], isLoading: isLoadingHomes } = useQuery({
    queryKey: ['/api/homes'],
  });

  // Handle both array and {total, items} formats for compatibility
  const organizations = Array.isArray(organizationsResponse) ? organizationsResponse : ((organizationsResponse as any)?.items ?? []);
  const homes = Array.isArray(homesResponse) ? homesResponse : ((homesResponse as any)?.items ?? []);

  if (!canAccessDeveloperFeatures()) {
    return (
      <div className="container mx-auto py-8" data-testid="access-denied-container">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Access denied. Only developers can manage template permissions.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const handleTogglePermission = async (scope_type: 'Organization' | 'Home', scope_id: number) => {
    if (hasPermission(scope_type, scope_id)) {
      revokePermission({ scope_type, scope_id });
    } else {
      grantPermission({ scope_type, scope_id });
    }
  };

  const getPermissionCount = (scope_type: 'Organization' | 'Home') => {
    return (permissions as any[])?.filter((p: any) => p.scope_type === scope_type).length ?? 0;
  };

  return (
    <div className="container mx-auto py-6 space-y-6" data-testid="template-permission-manager">
      <PageHeader 
        title="Template Permission Management"
        subtitle="Manage template creation permissions for organizations and care homes"
      />

      <div className="grid gap-6 md:grid-cols-3">
        <Card data-testid="stats-total-permissions">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Delegated Permissions</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-total-permissions">
              {isLoadingPermissions ? "..." : (permissions as any[])?.length ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stats-org-permissions">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Organization Permissions</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-org-permissions">
              {isLoadingPermissions ? "..." : getPermissionCount('Organization')}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stats-home-permissions">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Home Permissions</CardTitle>
            <Home className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-home-permissions">
              {isLoadingPermissions ? "..." : getPermissionCount('Home')}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList data-testid="permission-tabs">
          <TabsTrigger value="organizations" data-testid="tab-organizations">
            <Building className="h-4 w-4 mr-2" />
            Organizations
          </TabsTrigger>
          <TabsTrigger value="homes" data-testid="tab-homes">
            <Home className="h-4 w-4 mr-2" />
            Care Homes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="organizations" className="space-y-4">
          <Card data-testid="organizations-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Organizations Template Permissions
              </CardTitle>
              <CardDescription>
                Grant or revoke template creation permissions for organizations. When granted, all users within the organization can create templates.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingOrgs ? (
                <div className="flex items-center justify-center py-8" data-testid="loading-organizations">
                  <div className="text-muted-foreground">Loading organizations...</div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organization</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Permission</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organizations.map((org: Organization) => (
                      <TableRow key={org.id} data-testid={`row-organization-${org.id}`}>
                        <TableCell className="font-medium" data-testid={`text-org-name-${org.id}`}>
                          {org.name}
                        </TableCell>
                        <TableCell>
                          <Badge variant={org.status === 'Active' ? 'default' : 'secondary'} data-testid={`badge-org-status-${org.id}`}>
                            {org.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground" data-testid={`text-org-email-${org.id}`}>
                          {org.billing_email}
                        </TableCell>
                        <TableCell>
                          <Badge variant={hasPermission('Organization', org.id) ? 'default' : 'outline'} data-testid={`badge-org-permission-${org.id}`}>
                            {hasPermission('Organization', org.id) ? 'Granted' : 'Not Granted'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <Label htmlFor={`org-switch-${org.id}`} className="text-sm">
                              {hasPermission('Organization', org.id) ? 'Enabled' : 'Disabled'}
                            </Label>
                            <Switch
                              id={`org-switch-${org.id}`}
                              checked={hasPermission('Organization', org.id)}
                              onCheckedChange={() => handleTogglePermission('Organization', org.id)}
                              disabled={isGrantingPermission || isRevokingPermission}
                              data-testid={`switch-org-permission-${org.id}`}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="homes" className="space-y-4">
          <Card data-testid="homes-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="h-5 w-5" />
                Care Homes Template Permissions
              </CardTitle>
              <CardDescription>
                Grant or revoke template creation permissions for individual care homes. When granted, users specifically assigned to the home can create templates.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingHomes ? (
                <div className="flex items-center justify-center py-8" data-testid="loading-homes">
                  <div className="text-muted-foreground">Loading care homes...</div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Care Home</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Beds</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Permission</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {homes.map((home: HomeData) => (
                      <TableRow key={home.id} data-testid={`row-home-${home.id}`}>
                        <TableCell className="font-medium" data-testid={`text-home-name-${home.id}`}>
                          {home.name}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm" data-testid={`text-home-address-${home.id}`}>
                          {home.address}
                        </TableCell>
                        <TableCell data-testid={`text-home-beds-${home.id}`}>
                          {home.bed_count || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={home.is_active ? 'default' : 'secondary'} data-testid={`badge-home-status-${home.id}`}>
                            {home.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={hasPermission('Home', home.id) ? 'default' : 'outline'} data-testid={`badge-home-permission-${home.id}`}>
                            {hasPermission('Home', home.id) ? 'Granted' : 'Not Granted'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <Label htmlFor={`home-switch-${home.id}`} className="text-sm">
                              {hasPermission('Home', home.id) ? 'Enabled' : 'Disabled'}
                            </Label>
                            <Switch
                              id={`home-switch-${home.id}`}
                              checked={hasPermission('Home', home.id)}
                              onCheckedChange={() => handleTogglePermission('Home', home.id)}
                              disabled={isGrantingPermission || isRevokingPermission}
                              data-testid={`switch-home-permission-${home.id}`}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}